<template>
  <div class="base64-page">
    <!-- Hero Section -->
    <section class="hero-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <h1 class="hero-title">Base64 Encoder / Decoder</h1>
            <p class="hero-subtitle">
              Encode and decode text or files to/from Base64 format. Perfect for API development, data URIs, and data transmission.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Tool Section -->
    <section class="tool-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <!-- Mode Selector -->
            <div class="mode-selector mb-4">
              <button
                class="mode-btn"
                :class="{ active: mode === 'encode' }"
                @click="mode = 'encode'"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="16 18 22 12 16 6" />
                  <polyline points="8 6 2 12 8 18" />
                </svg>
                Encode
              </button>
              <button
                class="mode-btn"
                :class="{ active: mode === 'decode' }"
                @click="mode = 'decode'"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="8 18 2 12 8 6" />
                  <polyline points="16 6 22 12 16 18" />
                </svg>
                Decode
              </button>
            </div>

            <div class="row">
              <!-- Input -->
              <div class="col-lg-6 mb-4">
                <div class="io-card">
                  <div class="card-header">
                    <h3>{{ mode === 'encode' ? 'Plain Text' : 'Base64' }}</h3>
                    <div class="card-actions">
                      <button class="btn-action" @click="clearInput" title="Clear">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                      <button class="btn-action" @click="pasteFromClipboard" title="Paste">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                          <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="input"
                    class="io-textarea"
                    :placeholder="mode === 'encode' ? 'Enter text to encode...' : 'Enter Base64 to decode...'"
                    @input="process"
                  />
                  <div class="char-count">{{ input.length }} characters</div>
                </div>

                <!-- File Upload for Encode -->
                <div v-if="mode === 'encode'" class="file-upload-section mt-3">
                  <label class="file-upload-label">
                    <input type="file" @change="handleFileUpload" class="file-input" accept="image/*,text/*">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    Or upload file to encode
                  </label>
                </div>
              </div>

              <!-- Output -->
              <div class="col-lg-6 mb-4">
                <div class="io-card">
                  <div class="card-header">
                    <h3>{{ mode === 'encode' ? 'Base64' : 'Plain Text' }}</h3>
                    <div class="card-actions">
                      <button
                        class="btn-action"
                        :class="{ success: copied }"
                        @click="copyOutput"
                        title="Copy"
                      >
                        <svg v-if="!copied" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </button>
                      <button v-if="mode === 'decode' && isValidImage" class="btn-action" @click="downloadImage" title="Download Image">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="output"
                    class="io-textarea"
                    :class="{ error: error }"
                    readonly
                    :placeholder="mode === 'encode' ? 'Base64 output will appear here...' : 'Decoded text will appear here...'"
                  />
                  <div v-if="error" class="error-message">{{ error }}</div>
                  <div v-else class="char-count">{{ output.length }} characters</div>
                </div>

                <!-- Image Preview -->
                <div v-if="mode === 'decode' && isValidImage" class="image-preview mt-3">
                  <h4>Image Preview</h4>
                  <img :src="imagePreviewUrl" alt="Decoded image" class="preview-img">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Info Section -->
    <section class="info-section">
      <div class="container">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="info-card">
              <h3>About Base64 Encoding</h3>
              <p>
                Base64 is a binary-to-text encoding scheme that represents binary data in an ASCII string format. 
                It's commonly used to encode data that needs to be stored or transferred over media designed to handle text.
              </p>
              
              <h4>Common Use Cases</h4>
              <ul>
                <li><strong>Email Attachments</strong> - Encoding binary files in email (MIME)</li>
                <li><strong>Data URIs</strong> - Embedding images directly in HTML/CSS</li>
                <li><strong>API Requests</strong> - Transmitting binary data in JSON payloads</li>
                <li><strong>Basic Authentication</strong> - HTTP Basic Auth header encoding</li>
                <li><strong>File Storage</strong> - Storing binary data in text-based formats</li>
              </ul>
              
              <h4>How It Works</h4>
              <p>
                Base64 encoding converts binary data into a set of 64 ASCII characters (A-Z, a-z, 0-9, +, /).
                Every 3 bytes of binary data is converted to 4 Base64 characters, making the encoded data about 33% larger.
              </p>
              
              <h4>Example</h4>
              <p>
                <strong>Plain text:</strong> <code>Hello World!</code>
                <br>
                <strong>Base64:</strong> <code>SGVsbG8gV29ybGQh</code>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

useHead({
  title: 'Base64 Encoder/Decoder | Developer Tools',
  meta: [
    { name: 'description', content: 'Encode and decode text or files to/from Base64 format. Perfect for API development, data URIs, and data transmission.' }
  ]
})

const mode = ref<'encode' | 'decode'>('encode')
const input = ref('')
const output = ref('')
const error = ref('')
const copied = ref(false)
const isValidImage = ref(false)
const imagePreviewUrl = ref('')

const process = () => {
  error.value = ''
  isValidImage.value = false
  imagePreviewUrl.value = ''

  if (!input.value.trim()) {
    output.value = ''
    return
  }

  try {
    if (mode.value === 'encode') {
      output.value = btoa(input.value)
    } else {
      output.value = atob(input.value)
      
      // Check if it's an image
      if (input.value.startsWith('data:image/') || input.value.startsWith('/9j/') || input.value.startsWith('iVBOR')) {
        try {
          imagePreviewUrl.value = input.value.startsWith('data:') ? input.value : `data:image/png;base64,${input.value}`
          isValidImage.value = true
        } catch (e) {
          // Not a valid image
        }
      }
    }
  } catch (e) {
    error.value = mode.value === 'encode' 
      ? 'Error encoding text. Please check your input.'
      : 'Invalid Base64 string. Please check your input.'
    output.value = ''
  }
}

const clearInput = () => {
  input.value = ''
  output.value = ''
  error.value = ''
  isValidImage.value = false
  imagePreviewUrl.value = ''
}

const pasteFromClipboard = async () => {
  try {
    const text = await navigator.clipboard.readText()
    input.value = text
    process()
  } catch (err) {
    console.error('Failed to paste:', err)
  }
}

const copyOutput = async () => {
  if (!output.value) return
  
  try {
    await navigator.clipboard.writeText(output.value)
    copied.value = true
    setTimeout(() => {
      copied.value = false
    }, 2000)
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const handleFileUpload = (event: Event) => {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return

  const reader = new FileReader()
  reader.onload = (e) => {
    const result = e.target?.result as string
    if (file.type.startsWith('image/')) {
      // For images, get base64 from data URL
      input.value = result.split(',')[1]
    } else {
      // For text files
      input.value = result
    }
    process()
  }

  if (file.type.startsWith('image/')) {
    reader.readAsDataURL(file)
  } else {
    reader.readAsText(file)
  }
}

const downloadImage = () => {
  if (!isValidImage.value) return
  
  const link = document.createElement('a')
  link.href = imagePreviewUrl.value
  link.download = 'decoded-image.png'
  link.click()
}
</script>

<style scoped>
.base64-page {
  padding-top: 80px;
  min-height: 100vh;
  background: linear-gradient(to bottom, #f8f9fa 0%, #ffffff 100%);
}

.hero-section {
  padding: 4rem 0 2rem;
  text-align: center;
}

.hero-title {
  font-size: 3rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #4a5568;
  max-width: 700px;
  margin: 0 auto;
}

.tool-section {
  padding: 2rem 0 4rem;
}

.mode-selector {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.mode-btn {
  background: white;
  border: 2px solid #e2e8f0;
  padding: 1rem 2rem;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #4a5568;
  transition: all 0.2s ease;
}

.mode-btn:hover {
  border-color: #667eea;
  background: #f7fafc;
  transform: translateY(-2px);
}

.mode-btn.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: white;
}

.io-card {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.card-header h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  background: white;
  border: 1px solid #e2e8f0;
  padding: 0.5rem;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: #4a5568;
}

.btn-action:hover {
  border-color: #667eea;
  color: #667eea;
  background: #f7fafc;
}

.btn-action.success {
  border-color: #48bb78;
  color: #48bb78;
  background: #f0fff4;
}

.io-textarea {
  width: 100%;
  min-height: 300px;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-family: 'Courier New', monospace;
  font-size: 0.95rem;
  resize: vertical;
  transition: all 0.2s ease;
}

.io-textarea:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.io-textarea.error {
  border-color: #f56565;
}

.char-count {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #a0aec0;
  text-align: right;
}

.error-message {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #f56565;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.file-upload-section {
  text-align: center;
}

.file-upload-label {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: white;
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: #4a5568;
}

.file-upload-label:hover {
  border-color: #667eea;
  background: #f7fafc;
  color: #667eea;
}

.file-input {
  display: none;
}

.image-preview {
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.image-preview h4 {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 1rem;
}

.preview-img {
  max-width: 100%;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.info-section {
  padding: 4rem 0;
  background: white;
}

.info-card {
  padding: 2rem;
}

.info-card h3 {
  color: #1a202c;
  font-weight: 700;
  margin-bottom: 1rem;
}

.info-card h4 {
  color: #2d3748;
  font-weight: 600;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.info-card p {
  color: #4a5568;
  line-height: 1.7;
}

.info-card ul {
  color: #4a5568;
  line-height: 1.8;
}

.info-card code {
  background: #f7fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  color: #667eea;
  font-size: 0.9em;
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 2rem;
  }
  
  .mode-selector {
    flex-direction: column;
  }
  
  .mode-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
